import React, { useEffect, useMemo, useState } from 'react';
import { XCircleIcon, CheckCircleIcon } from './icons';
import type { PendingFacebookPage } from '../services/facebookIntegrationService';

interface FacebookPageSelectionModalProps {
  isOpen: boolean;
  pages: PendingFacebookPage[];
  loading: boolean;
  error: string | null;
  onConfirm: (selectedIds: string[]) => Promise<void>;
  onCancel: () => Promise<void>;
  onDismiss: () => void;
}

export const FacebookPageSelectionModal: React.FC<FacebookPageSelectionModalProps> = ({
  isOpen,
  pages,
  loading,
  error,
  onConfirm,
  onCancel,
  onDismiss,
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedIds([]);
      setSubmitError(null);
      setIsSubmitting(false);
      setIsCancelling(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (pages.length > 0) {
      setSelectedIds(pages.filter(page => !page.alreadyConnected).map(page => page.id));
    }
  }, [pages]);

  const handleToggle = (pageId: string) => {
    setSelectedIds(prev => (prev.includes(pageId) ? prev.filter(id => id !== pageId) : [...prev, pageId]));
  };

  const handleConfirm = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting || isCancelling) return;
    if (selectedIds.length === 0) {
      setSubmitError('Select at least one Page to continue.');
      return;
    }
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await onConfirm(selectedIds);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to connect selected Pages.';
      setSubmitError(message);
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(false);
  };

  const handleCancel = async () => {
    if (isSubmitting || isCancelling) return;
    setIsCancelling(true);
    try {
      await onCancel();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to cancel connection at this time.';
      setSubmitError(message);
      setIsCancelling(false);
      return;
    }
    setIsCancelling(false);
  };

  const displayError = submitError ?? error;

  const selectionSummary = useMemo(() => {
    const totalSelected = selectedIds.length;
    if (totalSelected === 0) return 'No Pages selected';
    if (totalSelected === 1) return '1 Page selected';
    return `${totalSelected} Pages selected`;
  }, [selectedIds]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-slate-900/50 dark:bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white dark:bg-gray-900 text-slate-900 dark:text-gray-100 rounded-2xl shadow-xl border border-slate-200 dark:border-gray-700 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-gray-700 px-6 py-4">
          <div>
            <h2 className="text-xl font-bold">Connect Facebook Pages</h2>
            <p className="text-sm text-slate-500 dark:text-gray-400">
              Select the Pages you want to authorise for scheduling posts. You can add more later.
            </p>
          </div>
          <button
            onClick={onDismiss}
            className="text-slate-400 hover:text-slate-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
            aria-label="Close"
            disabled={isSubmitting || isCancelling}
          >
            <XCircleIcon className="w-8 h-8" />
          </button>
        </div>

        <form onSubmit={handleConfirm} className="flex-1 flex flex-col">
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {loading ? (
              <div className="animate-pulse space-y-4">
                <div className="h-16 bg-slate-200 dark:bg-gray-700 rounded-xl" />
                <div className="h-16 bg-slate-200 dark:bg-gray-700 rounded-xl" />
                <div className="h-16 bg-slate-200 dark:bg-gray-700 rounded-xl" />
              </div>
            ) : (
              <div className="space-y-3">
                {pages.map(page => {
                  const isSelected = selectedIds.includes(page.id);
                  return (
                    <label
                      key={page.id}
                      className={`flex items-center justify-between border rounded-xl px-4 py-3 transition-colors ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-slate-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500'
                      }`}
                    >
                      <div>
                        <p className="font-semibold">{page.name}</p>
                        {page.alreadyConnected ? (
                          <p className="text-xs text-green-600 dark:text-green-300 flex items-center gap-1">
                            <CheckCircleIcon className="w-4 h-4" />
                            Already connected – selecting will refresh tokens
                          </p>
                        ) : (
                          <p className="text-xs text-slate-500 dark:text-gray-400">Not yet connected</p>
                        )}
                        {page.instagramAccount && (
                          <p className="text-xs text-slate-500 dark:text-gray-400">Linked Instagram: @{page.instagramAccount.username}</p>
                        )}
                      </div>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggle(page.id)}
                        className="w-5 h-5 accent-blue-500"
                        disabled={isSubmitting || isCancelling}
                      />
                    </label>
                  );
                })}
                {pages.length === 0 && !loading && (
                  <p className="text-sm text-slate-500 dark:text-gray-400">No eligible Facebook Pages were returned.</p>
                )}
              </div>
            )}
          </div>

          {displayError && (
            <div className="px-6 pb-2">
              <p className="text-sm text-red-500">{displayError}</p>
            </div>
          )}

          <div className="px-6 py-4 border-t border-slate-200 dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm text-slate-500 dark:text-gray-400">{selectionSummary}</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 rounded-lg border border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
                disabled={isSubmitting || isCancelling}
              >
                {isCancelling ? 'Cancelling...' : 'Cancel'}
              </button>
              <button
                type="submit"
                className="px-5 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isSubmitting || isCancelling || loading || pages.length === 0}
              >
                {isSubmitting ? 'Connecting...' : 'Connect Selected'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
